import React from 'react'
import { useHistory } from 'react-router-dom'

import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import {
  Box,
  Chip,
  Link,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'

import { AutocompleteTypeAndSearch, Select } from 'components'
import { DepartmentAutocomplete } from './components'

import helpers from 'helpers'

import usePermissions from 'hooks/usePermissions'
import useSourceOrigin from 'hooks/useSourceOrigin'

import constants from 'constants/index'
import * as service from 'service'
import useStyles from './styles'

const ActivityDetailsTab = ({
  columns,
  departments,
  activity,
  currentColumn = null,
  isMultiDepartment = false,
  isShow = false,
}) => {
  const {
    PRIORITY_ITEMS,
    KANBAN_COLUMN_ROLE_FINALIZED,
    SOURCEABLE_TYPE_COMPLAINT,
  } = constants.activities

  const classes = useStyles()
  const permissions = usePermissions()
  const history = useHistory()
  const { hasSourceOrigin, getSourceOriginPath, getSourceOriginLabel } =
    useSourceOrigin()

  const sourceOriginPath = getSourceOriginPath(activity)
  const sourceOriginLabel = getSourceOriginLabel(activity)
  const canManage =
    !isShow && permissions.permitted(constants.permissions.KANBAN_TASKS.MANAGE)
  const canEdit =
    !isShow &&
    permissions.some([
      constants.permissions.KANBAN_TASKS.MANAGE,
      constants.permissions.KANBAN_TASKS.FINALIZE,
    ])
  const { control, errors, getValues } = useFormContext()

  const canFinalize = permissions.permitted(
    constants.permissions.KANBAN_TASKS.FINALIZE,
  )

  const taskColumnInList = columns.find(
    (c) => c.id === activity?.kanbanColumnId,
  )
  const currentColumnRole = taskColumnInList?.role
  const isCrossBoard = !!activity?.targetCompanyId
  const isComplaintCard = activity?.sourceableType === SOURCEABLE_TYPE_COMPLAINT

  const statusItems = columns
    .filter(
      (col) =>
        col.role !== KANBAN_COLUMN_ROLE_FINALIZED ||
        canFinalize ||
        currentColumnRole === KANBAN_COLUMN_ROLE_FINALIZED,
    )
    .map((col) => ({
      id: col.id,
      name: col.title.length > 30 ? col.title.slice(0, 30) + '…' : col.title,
      color: col.color,
      textColor: col.textColor,
    }))

  const requestUsers = async (name) => {
    const res = await service.dponet.users.get({
      name,
      perPage: 10000000,
      minimal: true,
      order: 'name ASC',
    })
    return res?.data?.users || []
  }

  return (
    <Box display="flex" flexDirection="column">
      {activity?.sourceableId && (
        <Box className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Origem</Typography>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gridGap={4}
          >
            {sourceOriginLabel && (
              <Tooltip
                title={sourceOriginLabel.length > 28 ? sourceOriginLabel : ''}
              >
                <Chip
                  label={helpers.truncateUIText(sourceOriginLabel, 28)}
                  size="small"
                  className={classes.sourceChip}
                />
              </Tooltip>
            )}
            {hasSourceOrigin(activity) && (
              <Link
                component="button"
                underline="hover"
                onClick={() => history.push(sourceOriginPath)}
              >
                Ver origem
              </Link>
            )}
          </Box>
        </Box>
      )}

      {activity?.author && (
        <Box className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Criado por</Typography>
          <Typography variant="body2">
            {activity.author.name.length > 35
              ? activity.author.name.slice(0, 35) + '…'
              : activity.author.name}
          </Typography>
        </Box>
      )}

      {activity?.createdAt && (
        <Box className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>
            Data de Criação
          </Typography>
          <Typography variant="body2">
            {helpers.formatters.date(activity.createdAt)}
          </Typography>
        </Box>
      )}

      <Box className={classes.fieldRow}>
        <Typography className={classes.fieldLabel}>Responsável *</Typography>
        <AutocompleteTypeAndSearch
          name="responsibleIds"
          multiple
          defaultValue={getValues('responsibleIds') || []}
          requestToLoadOptions={requestUsers}
          disabled={!canManage || isCrossBoard || isComplaintCard}
          renderTags={(selectedUsers, getTagProps) =>
            selectedUsers.map((user, index) => (
              <Chip
                label={
                  <Tooltip title={user.name}>
                    <span className={classes.responsibleChipLabel}>
                      {user.name}
                    </span>
                  </Tooltip>
                }
                {...getTagProps({ index })}
              />
            ))
          }
          textField={{
            variant: 'outlined',
            size: 'small',
            error: !!errors?.responsibleIds,
            helperText:
              errors?.responsibleIds?.message ||
              (isComplaintCard
                ? 'Definidos pelo Canal de Denúncias'
                : undefined),
          }}
        />
      </Box>

      <Box className={classes.fieldRow}>
        <Typography className={classes.fieldLabel}>Prioridade *</Typography>
        <Controller
          as={
            <Select
              items={PRIORITY_ITEMS}
              error={!!errors?.priority}
              helperText={<>{errors?.priority?.message}</>}
              fullWidth
              disabled={!canManage || isCrossBoard}
            />
          }
          control={control}
          name="priority"
        />
      </Box>

      <Box className={classes.fieldRow}>
        <Typography className={classes.fieldLabel}>Status *</Typography>
        <Controller
          as={
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              error={!!errors?.kanbanColumnId}
              helperText={errors?.kanbanColumnId?.message}
              disabled={!canEdit || isCrossBoard}
              SelectProps={{
                renderValue: (val) =>
                  isCrossBoard
                    ? (taskColumnInList || currentColumn)?.title ?? ''
                    : statusItems.find((i) => i.id === val)?.name ?? '',
              }}
            >
              {statusItems.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Chip
                    size="small"
                    label={item.name}
                    style={{
                      backgroundColor: item.color,
                      color: item.textColor || '#FFFFFF',
                      fontWeight: 600,
                    }}
                  />
                </MenuItem>
              ))}
            </TextField>
          }
          control={control}
          name="kanbanColumnId"
        />
      </Box>

      <Box className={classes.fieldRow}>
        <Typography className={classes.fieldLabel}>Departamento</Typography>
        <Controller
          key={isMultiDepartment ? 'multi' : 'single'}
          as={
            <DepartmentAutocomplete
              multiple={isMultiDepartment}
              options={departments.map((dept) => ({
                ...dept,
                name: helpers.truncateUIText(dept.name, 30),
              }))}
              disabled={!canManage || isCrossBoard}
              error={!!errors?.departmentIds}
              helperText={errors?.departmentIds?.message}
            />
          }
          control={control}
          name="departmentIds"
          mode="onBlur"
          onChange={([, data]) =>
            isMultiDepartment ? data || [] : data ? [data] : []
          }
          defaultValue={[]}
        />
      </Box>

      <Box className={classes.fieldRow}>
        <Typography className={classes.fieldLabel}>Prazo *</Typography>
        <Controller
          control={control}
          name="dueDate"
          mode="onChange"
          as={
            <KeyboardDatePicker
              fullWidth
              format="DD/MM/yyyy"
              inputVariant="outlined"
              size="small"
              onChange={(newValue) => ({ value: newValue })}
              error={!!errors?.dueDate}
              helperText={errors?.dueDate?.message}
              clearable
              disabled={!canManage || isCrossBoard}
            />
          }
        />
      </Box>
    </Box>
  )
}

ActivityDetailsTab.propTypes = {
  columns: PropTypes.array,
  departments: PropTypes.array,
  activity: PropTypes.object,
  currentColumn: PropTypes.object,
  isMultiDepartment: PropTypes.bool,
  isShow: PropTypes.bool,
}

ActivityDetailsTab.defaultProps = {
  columns: [],
  departments: [],
  activity: null,
  currentColumn: null,
  isMultiDepartment: false,
}

export default ActivityDetailsTab
